import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';

import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '@/shared/ui';

import { useColdChainFields } from '../model/use-cold-chain-fields';
import type { ConsignmentLineDetailsFormValues } from '../model/types';

interface ColdChainFieldsProps {
  form: UseFormReturn<ConsignmentLineDetailsFormValues>;
  isViewMode: boolean;
}

export const ColdChainFields = ({ form, isViewMode }: ColdChainFieldsProps) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    formState: { errors }
  } = form;
  const { coldChain, isOther, profileOptions, applyProfileBounds } = useColdChainFields({ control, setValue });

  if (!coldChain) return null;

  return (
    <>
      <FormField
        label={t('newcmr.line.coldchain.range.label')}
        name="coldChainProfile"
        error={!isViewMode ? errors.coldChainProfile?.message : undefined}
        required>
        <Controller
          name="coldChainProfile"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                applyProfileBounds(value);
              }}
              disabled={isViewMode}>
              <SelectTrigger ref={field.ref} id="coldChainProfile" className="w-full">
                <SelectValue placeholder={t('validity.valueMissing.select')} />
              </SelectTrigger>
              <SelectContent>
                {profileOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormField>

      {isOther && (
        <FieldGroup>
          <FormField
            label={t('requestline.coldMinTemp.label')}
            name="coldMinTemp"
            error={!isViewMode ? errors.coldMinTemp?.message : undefined}>
            <Input
              {...register('coldMinTemp', { valueAsNumber: true })}
              type="number"
              step="1"
              id="coldMinTemp"
              placeholder={t('requestline.coldMinTemp.placeholder', 'Enter minimum temperature')}
              disabled={isViewMode}
            />
          </FormField>

          <FormField
            label={t('requestline.coldMaxTemp.label')}
            name="coldMaxTemp"
            error={!isViewMode ? errors.coldMaxTemp?.message : undefined}>
            <Input
              {...register('coldMaxTemp', { valueAsNumber: true })}
              type="number"
              step="1"
              id="coldMaxTemp"
              placeholder={t('requestline.coldMaxTemp.placeholder', 'Enter maximum temperature')}
              disabled={isViewMode}
            />
          </FormField>
        </FieldGroup>
      )}
    </>
  );
};
